import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { m, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { Container } from './Container';
import { useAnalytics } from '../../hooks/useAnalytics';
import { NewsletterForm } from './NewsletterForm';
import { onPricingClick } from '../../utils/compoundEvents';

const FooterWrapper = styled.footer<{ $isDark: boolean }>`
  background: #000000;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${props => props.theme.spacing[12]} 0 ${props => props.theme.spacing[6]};
  margin-top: auto;
  min-height: 400px;
  contain: layout style;
`;

const NewsletterButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  gap: ${props => props.theme.spacing[2]};
  margin-top: ${props => props.theme.spacing[4]};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  position: relative;
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    width: 100%;
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -150%;
    width: 450%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      transparent 35%,
      rgba(10, 93, 235, 0.4) 47%,
      rgba(59, 127, 255, 0.5) 50%,
      rgba(10, 93, 235, 0.4) 53%,
      transparent 65%,
      transparent 100%
    );
    animation: swoosh 5s ease-in-out infinite;
    z-index: 1;
    transform: skew(-15deg);
    filter: blur(0.5px);
    pointer-events: none;
  }

  @keyframes swoosh {
    0% {
      left: -150%;
      opacity: 0;
    }
    20% {
      opacity: 1;
    }
    80% {
      opacity: 1;
    }
    100% {
      left: 120%;
      opacity: 0;
    }
  }

  /* Ensure content is above the swoosh */
  > * {
    position: relative;
    z-index: 2;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
    color: rgba(255, 255, 255, 0.9);
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ModalOverlay = styled(m.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  z-index: 999998;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing[4]};
`;

const Modal = styled(m.div)<{ $isDark: boolean }>`
  background: ${props =>
    props.$isDark
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)'
      : '#ffffff'};
  border-radius: ${props => props.theme.borderRadius.xl};
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: ${props =>
    props.$isDark
      ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'};
  position: relative;
  z-index: 999999;
  border: ${props => (props.$isDark ? '1px solid rgba(255, 255, 255, 0.1)' : 'none')};
`;

const ModalHeader = styled.div<{ $isDark: boolean }>`
  padding: ${props => props.theme.spacing[8]} ${props => props.theme.spacing[6]}
    ${props => props.theme.spacing[4]};
  text-align: center;
  background: ${props => (props.$isDark ? 'transparent' : '#f8fafc')};
  border-bottom: 1px solid ${props => (props.$isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0')};
`;

const ModalTitle = styled.h3<{ $isDark: boolean }>`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => (props.$isDark ? '#f8fafc' : '#0f172a')};
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const ModalSubtitle = styled.p<{ $isDark: boolean }>`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => (props.$isDark ? 'rgba(148, 163, 184, 1)' : '#64748b')};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
`;

const ModalContent = styled.div<{ $isDark: boolean }>`
  padding: ${props => props.theme.spacing[6]};
  background: ${props => (props.$isDark ? 'transparent' : 'white')};
`;

const CloseButton = styled(m.button)<{ $isDark: boolean }>`
  position: absolute;
  top: ${props => props.theme.spacing[4]};
  right: ${props => props.theme.spacing[4]};
  background: ${props => (props.$isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')};
  border: none;
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  color: ${props => (props.$isDark ? 'rgba(148, 163, 184, 1)' : '#64748b')};

  &:hover {
    background: ${props => (props.$isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)')};
    color: ${props => (props.$isDark ? '#f8fafc' : '#0f172a')};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: ${props => props.theme.spacing[6]};
  margin-bottom: ${props => props.theme.spacing[8]};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const LinksGrid = styled.div`
  display: none;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing[6]} ${props => props.theme.spacing[8]};
    margin-top: ${props => props.theme.spacing[6]};
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing[4]};
  }
`;

const FooterSection = styled.div`
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const MobileFooterSection = styled.div`
  display: none;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: block;
  }
`;

const Logo = styled(Link)`
  display: inline-block;
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  margin-bottom: ${props => props.theme.spacing[4]};
  outline: none;

  &:focus {
    outline: none;
  }
`;

const LogoImage = styled.img<{ $isDark: boolean }>`
  height: 32px;
  width: auto;
  aspect-ratio: 151 / 32;
  max-width: 151px;
  filter: none;
`;

const LogoWrapper = styled.div`
  position: relative;
  height: 32px;
  width: 151px;
  flex-shrink: 0;
`;

const Tagline = styled.p<{ $isDark: boolean }>`
  color: rgba(255, 255, 255, 0.7);
  font-size: ${props => props.theme.typography.fontSize.sm};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
  max-width: 400px;
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const FooterTitle = styled.h3<{ $isDark: boolean }>`
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const FooterLinks = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const FooterLink = styled(Link)<{ $isDark: boolean }>`
  color: rgba(255, 255, 255, 0.7);
  font-size: ${props => props.theme.typography.fontSize.sm};
  transition: color ${props => props.theme.transitions.fast};
  outline: none;

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
  }

  &:focus {
    outline: none;
  }
`;

const FooterExternalLink = styled.a<{ $isDark: boolean }>`
  color: rgba(255, 255, 255, 0.7);
  font-size: ${props => props.theme.typography.fontSize.sm};
  transition: color ${props => props.theme.transitions.fast};
  outline: none;

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
  }

  &:focus {
    outline: none;
  }
`;

const FooterBottom = styled.div`
  padding-top: ${props => props.theme.spacing[6]};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
`;

const Copyright = styled.p<{ $isDark: boolean }>`
  color: rgba(255, 255, 255, 0.7);
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[3]};
  align-items: center;
`;

const SocialLink = styled.a<{ $isDark: boolean }>`
  color: rgba(255, 255, 255, 0.5);
  transition: all ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.05);

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Social Icons
const LinkedInIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const XIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const YouTubeIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const SubstackIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24">
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
  </svg>
);

const MailIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const CloseIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export const Footer: React.FC = () => {
  const { isDark } = useTheme();
  const { trackEvent } = useAnalytics();
  const [showModal, setShowModal] = useState(false);

  const formatFooterLabel = (label: string) =>
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const handleFooterNavClick = (label: string, path?: string) => {
    if (path === '/pricing') {
      onPricingClick();
    }
    trackEvent('navigation', 'click', `footer:${formatFooterLabel(label)}`);
  };

  const handleSocialClick = (platform: string) => {
    trackEvent('social', 'click', platform);
  };

  const handleNewsletterOpen = () => {
    trackEvent('cta', 'click', 'footer:newsletter_open');
    setShowModal(true);
  };

  const handleNewsletterClose = (source: 'overlay' | 'button') => {
    trackEvent('cta', 'click', `footer:newsletter_close_${source}`);
    setShowModal(false);
  };

  const footerLinks = {
    product: [
      { label: 'AI-Powered Launch', path: '/ai-powered-launch' },
      { label: 'Features', path: '/features' },
      { label: 'How It Works', path: '/how-it-works' },
      { label: 'Pricing', path: '/pricing' },
    ],
    solutions: [
      { label: 'AI Agent Marketplace', path: '/white-label-ai-agent-marketplace' },
      { label: 'Multi-Vendor Marketplace', path: '/product-marketplace' },
      { label: 'Services Marketplace', path: '/product-services' },
      { label: 'Booking System', path: '/product-bookings' },
      { label: 'E-commerce Store', path: '/product-store' },
      { label: 'View All Products', path: '/products' },
    ],
    company: [
      { label: 'About', path: '/about' },
      { label: 'Resources', path: '/resources' },
      { label: 'Marketplace Insights', path: '/marketplace-insights' },
      { label: 'Success Stories', path: '/success-stories' },
      { label: 'Contact', path: '/contact' },
    ],
    tools: [],
  };

  return (
    <FooterWrapper $isDark={isDark}>
      <Container>
        <AnimatePresence>
          {showModal && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => handleNewsletterClose('overlay')}
            >
              <Modal
                $isDark={isDark}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <CloseButton
                  $isDark={isDark}
                  onClick={() => handleNewsletterClose('button')}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <CloseIcon />
                </CloseButton>

                <ModalHeader $isDark={isDark}>
                  <ModalTitle $isDark={isDark}>Subscribe to CNXION Insights</ModalTitle>
                  <ModalSubtitle $isDark={isDark}>
                    Get marketplace strategies, AI innovations, and growth insights delivered
                    weekly.
                  </ModalSubtitle>
                </ModalHeader>
                <ModalContent $isDark={isDark}>
                  <NewsletterForm
                    source="footer_modal"
                    variant="stacked"
                    isDark={isDark}
                    autoFocus
                    buttonText="Subscribe"
                    placeholder="Enter your email address"
                    onSuccess={() => {
                      // Keep modal open to show success message
                    }}
                  />
                </ModalContent>
              </Modal>
            </ModalOverlay>
          )}
        </AnimatePresence>

        <FooterContent>
          {/* Desktop Layout */}
          <FooterSection>
            <Logo to="/" onClick={() => handleFooterNavClick('logo')}>
              <LogoWrapper>
                <AnimatePresence mode="wait">
                  <m.div
                    key={isDark ? 'dark' : 'light'}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <LogoImage
                      src="/cnxion-logo.svg"
                      alt="CNXION"
                      width={151}
                      height={32}
                      $isDark={isDark}
                    />
                  </m.div>
                </AnimatePresence>
              </LogoWrapper>
            </Logo>
            <Tagline $isDark={isDark}>
              At CNXION, we pioneer recursive AI-assisted design — continuously evolving your
              marketplace through intelligent feedback loops and automated optimisation.
            </Tagline>
            <SocialLinks>
              <SocialLink
                href="https://www.facebook.com/wearecnxion"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="Facebook"
                onClick={() => handleSocialClick('facebook')}
              >
                <FacebookIcon />
              </SocialLink>
              <SocialLink
                href="https://x.com/cnxion"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="X"
                onClick={() => handleSocialClick('twitter')}
              >
                <XIcon />
              </SocialLink>
              <SocialLink
                href="https://www.youtube.com/@wearecnxion"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="YouTube"
                onClick={() => handleSocialClick('youtube')}
              >
                <YouTubeIcon />
              </SocialLink>
              <SocialLink
                href="https://www.linkedin.com/company/cnxion"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="LinkedIn"
                onClick={() => handleSocialClick('linkedin')}
              >
                <LinkedInIcon />
              </SocialLink>
              <SocialLink
                href="https://wearecnxion.substack.com/"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="Substack"
                onClick={() => handleSocialClick('substack')}
              >
                <SubstackIcon />
              </SocialLink>
            </SocialLinks>
            <NewsletterButton onClick={handleNewsletterOpen}>
              <MailIcon />
              Subscribe to CNXION Insights
            </NewsletterButton>
          </FooterSection>

          <FooterSection>
            <FooterTitle $isDark={isDark}>Solutions</FooterTitle>
            <FooterLinks>
              {footerLinks.solutions.map(link => (
                <li key={link.path}>
                  <FooterLink
                    to={link.path}
                    $isDark={isDark}
                    onClick={() => handleFooterNavClick(link.label, link.path)}
                  >
                    {link.label}
                  </FooterLink>
                </li>
              ))}
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle $isDark={isDark}>Platform</FooterTitle>
            <FooterLinks>
              {footerLinks.product.map(link => (
                <li key={link.path}>
                  <FooterLink
                    to={link.path}
                    $isDark={isDark}
                    onClick={() => handleFooterNavClick(link.label, link.path)}
                  >
                    {link.label}
                  </FooterLink>
                </li>
              ))}
            </FooterLinks>
            <FooterTitle $isDark={isDark} style={{ marginTop: '1.5rem' }}>
              Company
            </FooterTitle>
            <FooterLinks>
              {footerLinks.company.map(link => (
                <li key={link.path}>
                  <FooterLink
                    to={link.path}
                    $isDark={isDark}
                    onClick={() => handleFooterNavClick(link.label, link.path)}
                  >
                    {link.label}
                  </FooterLink>
                </li>
              ))}
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle $isDark={isDark}>Contact</FooterTitle>
            <FooterLinks>
              <li>
                <FooterExternalLink
                  href="mailto:hello@wearecnxion.com"
                  $isDark={isDark}
                  onClick={() => handleFooterNavClick('contact_email')}
                >
                  hello@wearecnxion.com
                </FooterExternalLink>
              </li>
              <li>
                <FooterExternalLink
                  href="tel:+61730777234"
                  $isDark={isDark}
                  onClick={() => handleFooterNavClick('contact_phone_aus')}
                >
                  AUS: +61 7 3077 7234
                </FooterExternalLink>
              </li>
              <li>
                <FooterExternalLink
                  href="tel:+442080898473"
                  $isDark={isDark}
                  onClick={() => handleFooterNavClick('contact_phone_uk')}
                >
                  UK: +44 2080 898473
                </FooterExternalLink>
              </li>
              <li>
                <FooterExternalLink
                  href="tel:+15168881296"
                  $isDark={isDark}
                  onClick={() => handleFooterNavClick('contact_phone_us')}
                >
                  US: +1 516 888 1296
                </FooterExternalLink>
              </li>
            </FooterLinks>
          </FooterSection>

          {/* Mobile Layout */}
          <MobileFooterSection>
            <Logo to="/" onClick={() => handleFooterNavClick('logo')}>
              <LogoWrapper>
                <AnimatePresence mode="wait">
                  <m.div
                    key={isDark ? 'dark' : 'light'}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <LogoImage
                      src="/cnxion-logo.svg"
                      alt="CNXION"
                      width={151}
                      height={32}
                      $isDark={isDark}
                    />
                  </m.div>
                </AnimatePresence>
              </LogoWrapper>
            </Logo>
            <Tagline $isDark={isDark}>
              At CNXION, we pioneer recursive AI-assisted design — continuously evolving your
              marketplace through intelligent feedback loops and automated optimisation.
            </Tagline>
            <SocialLinks>
              <SocialLink
                href="https://www.facebook.com/wearecnxion"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="Facebook"
                onClick={() => handleSocialClick('facebook')}
              >
                <FacebookIcon />
              </SocialLink>
              <SocialLink
                href="https://x.com/cnxion"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="X"
                onClick={() => handleSocialClick('twitter')}
              >
                <XIcon />
              </SocialLink>
              <SocialLink
                href="https://www.youtube.com/@wearecnxion"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="YouTube"
                onClick={() => handleSocialClick('youtube')}
              >
                <YouTubeIcon />
              </SocialLink>
              <SocialLink
                href="https://www.linkedin.com/company/cnxion"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="LinkedIn"
                onClick={() => handleSocialClick('linkedin')}
              >
                <LinkedInIcon />
              </SocialLink>
              <SocialLink
                href="https://wearecnxion.substack.com/"
                target="_blank"
                rel="noopener noreferrer"
                $isDark={isDark}
                aria-label="Substack"
                onClick={() => handleSocialClick('substack')}
              >
                <SubstackIcon />
              </SocialLink>
            </SocialLinks>
            <NewsletterButton onClick={handleNewsletterOpen}>
              <MailIcon />
              Subscribe to CNXION Insights
            </NewsletterButton>

            <LinksGrid>
              <div>
                <FooterTitle $isDark={isDark}>Solutions</FooterTitle>
                <FooterLinks>
                  {footerLinks.solutions.map(link => (
                    <li key={link.path}>
                      <FooterLink
                        to={link.path}
                        $isDark={isDark}
                        onClick={() => handleFooterNavClick(link.label, link.path)}
                      >
                        {link.label}
                      </FooterLink>
                    </li>
                  ))}
                </FooterLinks>

                <FooterTitle $isDark={isDark} style={{ marginTop: '1.5rem' }}>
                  Platform
                </FooterTitle>
                <FooterLinks>
                  {footerLinks.product.map(link => (
                    <li key={link.path}>
                      <FooterLink
                        to={link.path}
                        $isDark={isDark}
                        onClick={() => handleFooterNavClick(link.label, link.path)}
                      >
                        {link.label}
                      </FooterLink>
                    </li>
                  ))}
                </FooterLinks>
              </div>

              <div>
                <FooterTitle $isDark={isDark}>Company</FooterTitle>
                <FooterLinks>
                  {footerLinks.company.map(link => (
                    <li key={link.path}>
                      <FooterLink
                        to={link.path}
                        $isDark={isDark}
                        onClick={() => handleFooterNavClick(link.label)}
                      >
                        {link.label}
                      </FooterLink>
                    </li>
                  ))}
                </FooterLinks>

                <FooterTitle $isDark={isDark} style={{ marginTop: '1.5rem' }}>
                  Contact
                </FooterTitle>
                <FooterLinks>
                  <li>
                    <FooterExternalLink
                      href="mailto:hello@wearecnxion.com"
                      $isDark={isDark}
                      onClick={() => handleFooterNavClick('contact_email')}
                    >
                      hello@wearecnxion.com
                    </FooterExternalLink>
                  </li>
                  <li>
                    <FooterExternalLink
                      href="tel:+61730777234"
                      $isDark={isDark}
                      onClick={() => handleFooterNavClick('contact_phone_aus')}
                    >
                      AUS: +61 7 3077 7234
                    </FooterExternalLink>
                  </li>
                  <li>
                    <FooterExternalLink
                      href="tel:+442080898473"
                      $isDark={isDark}
                      onClick={() => handleFooterNavClick('contact_phone_uk')}
                    >
                      UK: +44 2080 898473
                    </FooterExternalLink>
                  </li>
                  <li>
                    <FooterExternalLink
                      href="tel:+15168881296"
                      $isDark={isDark}
                      onClick={() => handleFooterNavClick('contact_phone_us')}
                    >
                      US: +1 516 888 1296
                    </FooterExternalLink>
                  </li>
                </FooterLinks>
              </div>
            </LinksGrid>
          </MobileFooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright $isDark={isDark}>
            © 2016-{new Date().getFullYear()} CNXION. All rights reserved. All prices are listed in
            USD.
          </Copyright>
          <Copyright $isDark={isDark} style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: 'inherit',
                textDecoration: 'none',
                transition: 'color 150ms ease-out',
                outline: 'none',
              }}
              onClick={() => handleFooterNavClick('privacy_policy')}
              onMouseEnter={e =>
                ((e.target as HTMLAnchorElement).style.color = 'rgba(255, 255, 255, 1)')
              }
              onMouseLeave={e =>
                ((e.target as HTMLAnchorElement).style.color = 'rgba(255, 255, 255, 0.7)')
              }
            >
              Privacy Policy
            </a>
            <span style={{ margin: '0 0.5rem', opacity: 0.5 }}>|</span>
            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: 'inherit',
                textDecoration: 'none',
                transition: 'color 150ms ease-out',
                outline: 'none',
              }}
              onClick={() => handleFooterNavClick('terms_of_service')}
              onMouseEnter={e =>
                ((e.target as HTMLAnchorElement).style.color = 'rgba(255, 255, 255, 1)')
              }
              onMouseLeave={e =>
                ((e.target as HTMLAnchorElement).style.color = 'rgba(255, 255, 255, 0.7)')
              }
            >
              Terms of Service
            </a>
            <span style={{ margin: '0 0.5rem', opacity: 0.5 }}>|</span>
            <a
              href="/cnxion-and-gdpr"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: 'inherit',
                textDecoration: 'none',
                transition: 'color 150ms ease-out',
                outline: 'none',
              }}
              onClick={() => handleFooterNavClick('gdpr_privacy')}
              onMouseEnter={e =>
                ((e.target as HTMLAnchorElement).style.color = 'rgba(255, 255, 255, 1)')
              }
              onMouseLeave={e =>
                ((e.target as HTMLAnchorElement).style.color = 'rgba(255, 255, 255, 0.7)')
              }
            >
              GDPR & Privacy
            </a>
          </Copyright>
        </FooterBottom>
      </Container>
    </FooterWrapper>
  );
};
